import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    AddressModel,
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    CONNECTION_ERROR_CODES,
    LocalStorageNotificationMaterialOrderModel,
    MATERIAL_ORDER_PRIORITY,
    MATERIAL_STATUS_CODE,
    MaterialCheckModel,
    MaterialModel,
    MaterialOrderModel,
    MaterialService,
    ModalClass,
    NumberModel,
    OdataMaterialService,
    SynchronizationService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { of, Subject, tap } from 'rxjs';
import { catchError, filter, finalize, mergeMap, take } from 'rxjs/operators';
import { MaterialOrderPricingComponent } from '../material-order-pricing/material-order-pricing.component';
import { MaterialPricingModel } from '../material-pricing/material-pricing.model';
import { MaterialOrderRowDisplayOptions } from './material-order-row-details/material-order-row-details.component';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import PROCESS_INDICATOR = ConfigConstants.PROCESS_INDICATOR;

@Component({
    selector: 'pace-material-order',
    templateUrl: './material-order.component.html',
    styleUrls: ['./material-order.component.scss'],
})
export class MaterialOrderComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public model: MaterialOrderModel;

    @Input()
    public displayMaterialPricing: boolean;

    @Input()
    public displayMaterialQuote: boolean;

    protected readonly faPlus = faPlus;
    public materialOrderForm: FormGroup;
    public materialOrderFormChildren: FormArray;
    public displayAddressDetails: boolean;
    public readonly displayStorageLocation: boolean;
    public readonly displayAvailable: boolean;
    public readonly displayTs: boolean;
    public readonly displayNdf: boolean;
    public readonly displayRoom: boolean;
    public readonly displayBuilding: boolean;
    public readonly displayFloor: boolean;
    public readonly displayMaterialLabel: boolean;
    public readonly globalStorageLocation: string;
    public displayRepairPrice: boolean;
    public displayCustomerPrice: boolean;
    public rowDisplayOptions: MaterialOrderRowDisplayOptions;
    public isLoading = false;
    public sparePartsCatalogLink: string;
    private destroy$ = new Subject<void>();
    private materialModels: MaterialModel[] = [];

    private readonly matPrioritiesForDOP: string;
    private readonly matPrioritiesForCustomer: string;
    public csmlPhoneNumber: string;
    public enableTextInfoForDop: boolean;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        public readonly modalInstance: BsModalRef,
        private readonly appNotificationService: AppNotificationService,
        private readonly synchronizationService: SynchronizationService,
        private readonly odataMaterialService: OdataMaterialService,
        private readonly materialService: MaterialService,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.displayStorageLocation = this.clientManager.isNgmP58OrSA();
        this.displayAvailable = this.clientManager.isNgmP58OrSA();
        this.displayTs = this.clientManager.isNgmP58OrSA();
        this.displayNdf = this.clientManager.isNgmP58OrSA();
        this.displayRoom = this.clientManager.isNgmP58();
        this.displayBuilding = !this.clientManager.isNgmSA();
        this.displayFloor = !this.clientManager.isNgmSA();
        this.displayMaterialLabel = this.clientManager.isNgmAA();
        this.displayAddressDetails = false;
        this.matPrioritiesForDOP = this.configService.getMatPrioritiesForDOP();
        this.matPrioritiesForCustomer = this.configService.getMatPrioritiesForCustomer();
        this.sparePartsCatalogLink = this.configService.getSparePartsCatalogLink();
        this.globalStorageLocation = this.configService.getGlobalStorageLocationMatRequest();
        this.enableTextInfoForDop = this.configService.getEnableTextInfoForDop();
    }

    public ngOnInit(): void {
        this.displayRepairPrice = this.clientManager.isNgmSA() || this.displayMaterialPricing;
        this.displayCustomerPrice = this.clientManager.isNgmP58() ? this.displayMaterialPricing : true;
        this.rowDisplayOptions = {
            displayTs: this.displayTs,
            displayNdf: this.displayNdf,
            displayStorageLocation: this.displayStorageLocation,
            displayAvailable: this.displayAvailable,
            displayMaterialLabel: this.displayMaterialLabel,
            displayCustomerPrice: this.displayCustomerPrice,
            displayRepairPrice: this.displayRepairPrice,
        };

        const materialOrderValidator = (): ValidationErrors | null => this.checkValidity();
        this.materialOrderFormChildren = this.formBuilder.array([]);
        this.materialOrderForm = this.formBuilder.group(
            {
                children: this.materialOrderFormChildren,
            },
            {
                validators: materialOrderValidator,
            },
        );
        this.csmlPhoneNumber =
            this.model.notificationModel.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO
                ? this.configService.getCSMLInvitroPhoneNumber()
                : this.configService.getCSMLInvivoPhoneNumber();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public ngAfterViewInit(): void {
        this.setDeliveryOptions();
        this.updateDisplayAddressDetails();
        this.setStorageLocation();
    }

    public onPriorityChange(): void {
        this.setDeliveryOptions();
        if (this.configService.getDeliveryOptionsEnabled()) {
            this.model.updateCustomerApproverVisibility();
        }
        this.model.updateTextInfoOptions();
    }

    public onCityChange(): void {
        if (!this.model.enableStatesOfUSADropdown) {
            return;
        }
        this.log.log(`MaterialOrderComponent:onCityChange: Change in city field, deselecting statesOfUSA dropdown`);
        this.model.address.statesOfUSA.deselect();
        AddressModel.updateStateOfUSA(this.model.address);
    }

    /**
     * This function will open Material Pricing screen once the user clicks on 'add' button,
     * and the resulting material partset from pricing screen will be added into it.
     */
    public addMaterials(): void {
        const materialPricingModal = this.modalService.show(MaterialOrderPricingComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialPricingModel(this.model.notificationModel, this.model.partSet),
                displayRepairPrice: this.displayRepairPrice,
                displayCustomerPrice: this.displayCustomerPrice,
                displayMaterialQuote: this.displayMaterialQuote,
            },
            ariaLabelledBy: 'material-order-pricing-modal-title',
        });

        materialPricingModal.content?.materialOrderPricingAdded.subscribe((result: MaterialCheckModel[]) => {
            if (result.length > 0) {
                this.model.partSet.value = result;
                this.setStorageLocation();
                // Based on priority code, material quantity needs to be displayed
                this.setMaterialOptions();
            }
        });
    }

    private setStorageLocation(): void {
        this.model.partSet.value.forEach((material) => {
            if (material.storageLoc.value === '' && this.globalStorageLocation.length > 0) {
                material.storageLoc.value = this.globalStorageLocation;
            }
        });
    }

    /**
     * delete the materials from the partset list
     *
     * @param index index of the material to be removed
     */
    public deleteMaterial(index: number): void {
        this.model.partSet.removeModel(index);
    }

    /**
     * If Priority selected is Taxi, then Material Quantity value, Should be 1 with ReadOnly
     * If Quantity is > 1 , then in SplitMaterialByQuantity() we are creating child Material of each with Quantity 1
     * and we are updating the quantity 1 to the parent material
     */
    public setMaterialOptions(): void {
        if (!this.clientManager.isNgmP58()) {
            return;
        }
        const shouldSplitMaterial = this.configService.isMaterialOrderPriorityForSplit(
            this.model.priority.selectedEntry?.value,
        );

        let splitMaterialList: MaterialCheckModel[] = [];
        this.model.partSet.value.forEach((material) => {
            if (shouldSplitMaterial) {
                material.quantity = assignModelOptions(
                    material.quantity,
                    {
                        ...material.quantity.options,
                        readonly: true,
                    },
                    material,
                );
                if (material.quantity.value ?? 0 > 1) {
                    splitMaterialList = splitMaterialList.concat(
                        MaterialOrderComponent.splitMaterialByQuantity(material),
                    );
                    const quantity = _.cloneDeep(material.quantity);
                    quantity.value = 1;
                    material.quantity = <NumberModel>material.set(quantity);
                }
            } else {
                material.quantity = assignModelOptions(
                    material.quantity,
                    {
                        ...material.quantity.options,
                        readonly: false,
                    },
                    material,
                );
            }
        });
        this.model.partSet.value.push(...splitMaterialList);
    }

    /**
     * Material Row will be split based on number of Quantity
     * Here , we are creating child Material of each with Quantity 1
     * Example: Priority Taxi is selected and Quantity is 5 then it should split materials 5 times with Quantity 1 each.
     *
     * @param material the material to be split
     * @returns array of child materials
     */
    private static splitMaterialByQuantity(material: MaterialCheckModel): MaterialCheckModel[] {
        const totalQuantity = material.quantity.value ?? 0;
        const childMaterialList = [];

        for (let i = 1; i < totalQuantity; i++) {
            const childMaterial = _.cloneDeep(material);
            childMaterial.quantity.value = 1;
            childMaterialList.push(childMaterial);
        }

        return childMaterialList;
    }

    /**
     * This function will set deliveryOptions and material quantity based on priority code selected
     */
    public setDeliveryOptions(): void {
        this.setMaterialOptions();
        if (!this.matPrioritiesForDOP && !this.matPrioritiesForCustomer) {
            return;
        }
        const selectedEntry = this.model.priority.selectedEntry;
        // For priority Code 1 and 6 only DOP allowed, for priority Code 0 and 2-5, only Customer allowed
        if (selectedEntry) {
            let deliveryAddress: string;
            if (this.matPrioritiesForDOP.indexOf(selectedEntry.model.value) !== -1) {
                deliveryAddress = this.model.DOP_ADDRESS;
                this.model.deliveryOptions = assignModelOptions(
                    this.model.deliveryOptions,
                    {
                        ...this.model.deliveryOptions.options,
                        readonly: true,
                    },
                    this.model,
                );
            } else if (this.matPrioritiesForCustomer.indexOf(selectedEntry.model.value) !== -1) {
                deliveryAddress = this.model.CUSTOMER_ADDRESS;
                this.model.deliveryOptions = assignModelOptions(
                    this.model.deliveryOptions,
                    {
                        ...this.model.deliveryOptions.options,
                        readonly: true,
                    },
                    this.model,
                );
            } else {
                this.model.deliveryOptions = assignModelOptions(
                    this.model.deliveryOptions,
                    {
                        ...this.model.deliveryOptions.options,
                        readonly: false,
                    },
                    this.model,
                );
            }
            // SHCNGMP58-2337: Special handling for Austria invivo
            if (
                this.clientManager.isNgmP58Austria() &&
                this.model.notificationModel.processIndicator === PROCESS_INDICATOR.INVIVO
            ) {
                if (selectedEntry.model.value === '1') {
                    // DHL - End of business day
                    deliveryAddress = this.model.CUSTOMER_ADDRESS;
                    this.model.deliveryOptions = assignModelOptions(
                        this.model.deliveryOptions,
                        {
                            ...this.model.deliveryOptions.options,
                            readonly: true,
                        },
                        this.model,
                    );
                } else if (selectedEntry.model.value === '2') {
                    // NOX - overnight - invivo"
                    deliveryAddress = this.model.DOP_ADDRESS;
                    this.model.deliveryOptions = assignModelOptions(
                        this.model.deliveryOptions,
                        {
                            ...this.model.deliveryOptions.options,
                            readonly: false,
                        },
                        this.model,
                    );
                }
            }
            const deliveryOptions = _.cloneDeep(this.model.deliveryOptions);
            deliveryOptions.selectByPredicate((option) => option.model.name === deliveryAddress);
            this.model.deliveryOptions = deliveryOptions;
            this.model.updateAddress();
            this.updateDisplayAddressDetails();
        }
    }

    public get isTextInfoReadonly(): boolean {
        return this.model.isDopAddress && !this.enableTextInfoForDop;
    }

    public updateDisplayAddressDetails(): void {
        // Decide if address details are to be displayed
        if (this.clientManager.isNgmAA()) {
            // NGMAA: Display only for customer as DOP list contains also the private addresses of CSEs (SHSNGMAA-316)
            this.displayAddressDetails =
                this.model.deliveryOptions.selectedEntry.model.name === this.model.CUSTOMER_ADDRESS;
        } else {
            // P58: Display for customer, DOP
            this.displayAddressDetails = this.model.deliveryOptions.selectedEntry.model.name !== this.model.NO_ADDRESS;
        }
    }

    public validateForm(): void {
        this.model.updateAddress();
        this.changeDetector.detectChanges();
    }

    /**
     * function for creating material order
     *
     */
    public createMaterialOrder(): void {
        if (this.clientManager.isNgmAA() && this.model.partSet.value.length > ConfigConstants.maxMaterialOrderItems) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('MATERIAL_ORDER_MAX_ITEMS_TITLE'),
                <string>this.translate.instant('MATERIAL_ORDER_MAX_ITEMS_BODY', {
                    maxQuantity: ConfigConstants.maxMaterialOrderItems,
                }),
            );
            return;
        }
        // loading progress bar should only be initialized after there is no error
        this.isLoading = true;
        this.materialService
            .isSavedMaterialOrderOutOfSync()
            .pipe(
                take(1),
                tap((outOfSync) => {
                    if (outOfSync) {
                        this.modalInstance.hide();
                        this.modalService.show(YesNoModalDialogComponent, {
                            backdrop: 'static',
                            class: ModalClass.MODAL_MEDIUM,
                            initialState: {
                                model: new YesNoModalDialogModel(
                                    <string>this.translate.instant('MATERIAL_ORDER_OUT_OF_SYNC_TITLE'),
                                    <string>this.translate.instant('MATERIAL_ORDER_OUT_OF_SYNC_MESSAGE'),
                                    <string>this.translate.instant('MATERIAL_ORDER_OUT_OF_SYNC_ACTION'),
                                ),
                            },
                            ariaLabelledBy: 'dashboard-button-modal-title',
                        });
                    }
                }),
                filter((outOfSync) => !outOfSync),
                mergeMap(() => this.odataMaterialService.saveMaterialOrder(this.model)),
                mergeMap(() => {
                    const materialModels = this.model.partSet.value.map((materialCheckModel) => {
                        const newMaterialModel = MaterialModel.fromMaterialCheckModel(
                            materialCheckModel,
                            this.model.notificationModel,
                        );
                        newMaterialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.REQUESTED;
                        newMaterialModel.activityDate.value = moment();
                        newMaterialModel.statusString.value = 'MATERIAL_ORDER_REQUESTED_LABEL';
                        newMaterialModel.deliveryReturnSelectedOption =
                            this.model.deliveryOptions.selectedEntry.displayName;
                        newMaterialModel.orderPriority = this.model.priority.selectedEntry.displayName;
                        newMaterialModel.orderInfoText = this.model.textInfo.value;
                        return newMaterialModel;
                    });
                    // Material can only be ordered per notification per task number.
                    return this.materialService.saveMaterialCreateOrders(
                        new LocalStorageNotificationMaterialOrderModel(
                            this.model.notificationModel.identifier,
                            this.model.priority.selectedEntry
                                ? <MATERIAL_ORDER_PRIORITY>this.model.priority.selectedEntry.value
                                : MATERIAL_ORDER_PRIORITY.UNKNOWN,
                            materialModels,
                            this.model.address,
                            this.model.textInfo.value,
                            this.model.deliveryOptions.selectedEntry.value,
                            true,
                            Date.now(),
                            this.model.customerApprover?.value,
                        ),
                    );
                }),
                mergeMap(() => {
                    this.log.debug('createMaterialOrder -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(
                        `MaterialOrderComponent: createMaterialOrder: Error while sending request: ${JSON.stringify(
                            error,
                        )}`,
                        `${this.model.getOutModel()}`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('MATERIAL_REQUEST_ERROR_TITLE'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : <string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.modalInstance.hide();
                }),
            )
            .subscribe();
    }

    public checkValidity(): ValidationErrors | null {
        const errors: ValidationErrors = {};

        if (this.model.deliveryOptions.selectedEntry.model.name === this.model.NO_ADDRESS) {
            errors['no-address'] = 'No address selected!';
        }

        if (!this.model.partSet.value.length) {
            errors['no-material'] = 'No material added';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public saveMaterialOrderData(): void {
        this.materialModels = this.model.partSet.value.map((materialCheckModel) =>
            MaterialModel.fromMaterialCheckModel(materialCheckModel, this.model.notificationModel),
        );

        this.materialService
            .saveMaterialCreateOrders(
                new LocalStorageNotificationMaterialOrderModel(
                    this.model.notificationModel.identifier,
                    this.model.priority.selectedEntry
                        ? <MATERIAL_ORDER_PRIORITY>this.model.priority.selectedEntry.value
                        : MATERIAL_ORDER_PRIORITY.UNKNOWN,
                    this.materialModels,
                    this.model.address,
                    this.model.textInfo.value,
                    this.model.deliveryOptions.selectedEntry.name,
                    false,
                    Date.now(),
                    this.model.customerApprover?.value,
                ),
            )
            .pipe(
                take(1),
                catchError((error) => {
                    this.log.error(
                        `MaterialOrderComponent: saveMaterialOrderData: Error while saving the material order data: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
        this.modalInstance.hide();
    }

    public onSubAddressChange(): void {
        this.model.checkMaxCharValidationOnSubAddress();
    }

    public onContactChange(): void {
        this.model.checkMaxCharValidationOnContact();
    }
}
